/**
 * client/src/pages/SignIn.tsx — the second card's other side. §11.1.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * IT IS NOT ON THE FAMILY PATH, AND THAT IS WHY IT MAY SPEAK PLAINLY
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * §3.1: the second card "is an identity action rather than a taxonomy, and the first card works
 * completely without ever touching it." Nobody is routed here to make a voice. The two ways to
 * arrive are pressing "Sign in" on the door, and pressing "Keep it" after the payoff — and in the
 * second case there is already a recording, an actor, and a claim token, so nothing is at risk while
 * this form is filled in.
 *
 * That is what makes it safe for this screen to use ordinary words. `FAMILY.keep` covers the *offer*
 * and `copy.ts` deliberately has no sign-in form copy, because a sign-in form is not something a
 * 68-year-old is asked to do in order to start.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * TWO FACTS IN THE COPY THAT PEOPLE GET WRONG OTHERWISE
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *  1. **One karti.ai account.** §11.1: one shared Supabase project (`gttcxopjeswqyumedhlj`) for six
 *     sibling sites, so somebody arriving from books or audiobook must use their existing password.
 *     Signup refuses a conflicting address rather than adopting it — "adopt the conflicting auth
 *     user" is an account-takeover primitive for anyone holding the shared invite code, and
 *     og.karti.ai shipped exactly that. Saying so beforehand is the difference between "it linked"
 *     and "it's broken".
 *  2. **Public signup is closed and the invite is checked first.** An ungated form lets a bot burn
 *     the auth budget *for the whole karti.ai family*.
 *
 * ⚠️ **VOX-LAW 21. Only `"ok"` and `"wrong"` advance the auth state machine.** A throttled or
 * conflicting upstream returns 503, never a new user — and "wrong password" and "the auth provider
 * is down" are never the same sentence on this screen. Telling somebody their password is wrong when
 * Supabase is throttled sends them off to reset a password that was fine, and the reset goes through
 * the same throttled bucket.
 */

import { useCallback, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError, api, describeForConsole } from "../lib/api.ts";
import { useSession } from "../lib/session.tsx";
import { Button, ConsoleFrame, Field, Notice } from "../ui/primitives.tsx";

type Mode = "signin" | "signup";

export function SignIn() {
  const { adopt } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [mode, setMode] = useState<Mode>("signin");
  const [trouble, setTrouble] = useState<{ title: string; fix?: string } | null>(null);
  const busy = useRef(false);

  const [who, setWho] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [handle, setHandle] = useState("");
  const [invite, setInvite] = useState("");

  const submit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (busy.current) return;
      busy.current = true;
      setTrouble(null);
      try {
        const env =
          mode === "signin"
            ? await api.signIn(who.trim(), password)
            : await api.signUp({
                email: email.trim(),
                handle: handle.trim().toLowerCase(),
                password,
                invite: invite.trim(),
              });
        // Adopt rather than re-fetch: the sign-in response IS a `SessionEnvelope`, so a second
        // `GET /api/v1/session` here would be a round trip that can only tell us what we already
        // hold — and, on the one route that mints identities, a second chance to mint one.
        adopt(env);
        navigate(from, { replace: true });
      } catch (err) {
        setTrouble(troubleFor(err));
        busy.current = false;
      }
    },
    [mode, who, password, email, handle, invite, adopt, navigate, from],
  );

  return (
    <ConsoleFrame>
      <section className="vox-auth">
        <header className="vox-auth-intro">
          <p className="vox-step">One karti.ai account</p>
          <h1>{mode === "signin" ? "Welcome back." : "Make an account."}</h1>
          <p className="vox-lead">
            Your audiobook, music, games, and voice sign-in are the same. Use the email and password
            you already know; your voice profile will be connected the first time you arrive.
          </p>
          <p className="vox-quiet">
            You never need an account to record. Signing in is for keeping and managing what belongs
            to you.
          </p>
        </header>

        <div className="vox-auth-panel">
          <form className="vox-auth-form" onSubmit={(e) => void submit(e)}>
            {mode === "signin" ? (
              <>
                <Field
                  label="Email or handle"
                  value={who}
                  onChange={(e) => setWho(e.currentTarget.value)}
                  autoComplete="username"
                  required
                />
                <Field
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  autoComplete="current-password"
                  required
                />
              </>
            ) : (
              <>
                {/* The invite is validated before shared auth is contacted. */}
                <Field
                  label="Invite code"
                  value={invite}
                  onChange={(e) => setInvite(e.currentTarget.value)}
                  help="This site is invite-only. Ask Karti for the code."
                  autoComplete="off"
                  required
                />
                <Field
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  autoComplete="email"
                  required
                />
                <Field
                  label="Handle"
                  value={handle}
                  onChange={(e) => setHandle(e.currentTarget.value)}
                  help="2–24 characters: lowercase letters, numbers, dot, dash, underscore."
                  autoComplete="username"
                  required
                />
                <Field
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  autoComplete="new-password"
                  required
                />
              </>
            )}

            {trouble && (
              <Notice hard>
                <span>
                  <strong>{trouble.title}</strong>
                  {trouble.fix && (
                    <>
                      <br />
                      {trouble.fix}
                    </>
                  )}
                </span>
              </Notice>
            )}

            <div className="vox-btn-row">
              <Button type="submit" variant="primary">
                {mode === "signin" ? "Sign in" : "Make an account"}
              </Button>
          {/*
            ⚠️ **Switching mode clears the trouble AND the password, and not clearing them put a red
            alarm on an untouched form.** Measured: fail a sign-in, press "I need an account", and the
            signup form renders with *"That email or password isn't right."* in a large destructive
            box directly above "Make an account" — about a submission that had not happened, on a form
            with different fields — with the mistyped password still sitting in the password input.
            Her first impression of making an account is being told she got something wrong.

            The password goes too, and not only for tidiness: `autoComplete` is `current-password` on
            one form and `new-password` on the other, so a value carried across is a password manager
            being offered the wrong secret to save.
          */}
              <Button
                variant="quiet"
                onClick={() => {
                  setTrouble(null);
                  setPassword("");
                  setMode(mode === "signin" ? "signup" : "signin");
                }}
              >
                {mode === "signin" ? "I need an account" : "I already have one"}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </ConsoleFrame>
  );
}

/**
 * A refusal names what it refused and what would fix it. AGENTS.md's rule, in one table.
 *
 * ⚠️ **503 is never spelled "wrong password".** VOX-LAW 21 makes the auth provider being throttled or
 * conflicted a 503 rather than a `wrong`, and this function is where that distinction either survives
 * or is thrown away.
 */
function troubleFor(err: unknown): { title: string; fix?: string } {
  if (err instanceof ApiError) {
    if (err.offline) {
      return { title: "That request never reached the server.", fix: "Check the connection and try again." };
    }
    if (err.upstreamDown) {
      return {
        title: "The sign-in service isn't answering right now.",
        fix: "This is not your password. Wait a minute and try again — resetting it would go through the same queue.",
      };
    }
    switch (err.code) {
      case "wrong":
        return { title: "That email or password isn't right." };
      case "invite-required":
      case "invite-invalid":
        return { title: "That invite code isn't right.", fix: "Check it for a stray space, and mind the case." };
      case "email-taken":
        return {
          title: "There is already a karti.ai account with that email.",
          fix: "Sign in with it instead — the password is the one you use on the other sites.",
        };
      case "handle-taken":
        return { title: "That handle is taken.", fix: "Pick another." };
      default:
        return { title: err.detail };
    }
  }
  const d = describeForConsole(err);
  return { title: d.message };
}
